import { useMemo, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@workspace/ui/components/ui/popover";
import { listingsApi } from "@/lib/listings-api";
import { useAlgoliaColours } from "@/hooks/useAlgoliaSearch";
import { cn } from "@/lib/utils";

interface ColourOption {
  id: number;
  name: string;
  hex_code: string | null;
}

export interface ColourFilterProps {
  /**
   * `field` is the full-width control used inside the desktop sidebar and
   * mobile filter sheet (renders a label above the trigger to match the
   * other sidebar controls). `pill` is the compact top-bar variant that
   * sits next to the rental-type pills above the results.
   */
  variant?: "field" | "pill";
}

/**
 * Multi-select Colour filter (task #1835). Lists every active row in
 * `car_colours` (fetched via the public `/api/listings/lookup` endpoint)
 * with a swatch + name + checkbox. Selection is OR-semantic and stored
 * in Algolia's `colour_id` refinement; the URL serialises selected ids
 * via the `colours` query param (handled in AlgoliaSearchProvider).
 *
 * The control is a popover so it stays consistent with other compact
 * filter triggers (e.g. dates) and works in both the sidebar and the
 * top-bar pill row. Per-colour Algolia facet counts are shown when
 * available, and items with a zero count under the current filter set
 * are dimmed and disabled so users don't pick a refinement that
 * produces no hits.
 */
export function ColourFilter({ variant = "field" }: ColourFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { items, refinedColourIds, toggleColour, clear } = useAlgoliaColours();

  const { data: lookup } = useQuery({
    queryKey: ["listings-lookup-colours"],
    queryFn: () => listingsApi.getLookup(),
    staleTime: 60 * 60 * 1000,
  });

  const colours: ColourOption[] = useMemo(
    () => lookup?.colours ?? [],
    [lookup],
  );

  const colourById = useMemo(() => {
    const out: Record<string, ColourOption> = {};
    for (const c of colours) out[String(c.id)] = c;
    return out;
  }, [colours]);

  // Facets supply counts only; selected state comes from the refinement.
  const facetByValue = useMemo(() => {
    const m: Record<string, { count: number }> = {};
    for (const it of items) {
      m[String(it.value)] = { count: it.count };
    }
    return m;
  }, [items]);

  const label = t("search.filter_labels.colour", "Colour");
  const selectedCount = refinedColourIds.length;

  // Trigger summary: `Colour` → `Colour · Red` (one) → `Colour · 3 selected`
  const summary = useMemo(() => {
    if (selectedCount === 0) return label;
    if (selectedCount === 1) {
      const id = refinedColourIds[0];
      const name = colourById[id]?.name || id;
      return `${label} · ${name}`;
    }
    return `${label} · ${t("search.filter_labels.colour_count", {
      defaultValue: "{{count}} selected",
      count: selectedCount,
    })}`;
  }, [selectedCount, refinedColourIds, colourById, label, t]);

  // Up to 4 visible swatches in the trigger; show "+N" when more are
  // selected so the trigger stays compact.
  const swatchPreview = useMemo(() => {
    const max = 4;
    const visible = refinedColourIds.slice(0, max);
    const overflow = refinedColourIds.length - visible.length;
    return { visible, overflow };
  }, [refinedColourIds]);

  if (colours.length === 0) return null;

  const triggerClass =
    variant === "field"
      ? cn(
          "flex w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-3 py-2 text-left text-sm transition-colors",
          "hover:border-brand focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
        )
      : cn(
          "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200",
          selectedCount > 0
            ? "border-primary bg-primary/10 text-foreground"
            : "border-border bg-background text-foreground hover:bg-muted",
        );

  const trigger = (
    <button
      type="button"
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-testid="colour-filter-trigger"
      className={triggerClass}
    >
      <span className="flex items-center gap-2 truncate">
        <span className={selectedCount > 0 ? "text-foreground" : "text-muted-foreground"}>
          {summary}
        </span>
        {swatchPreview.visible.length > 0 && (
          <span className="flex items-center -space-x-1" aria-hidden>
            {swatchPreview.visible.map((id) => (
              <span
                key={id}
                className="inline-block h-4 w-4 rounded-full border border-background ring-1 ring-border"
                style={{ backgroundColor: colourById[id]?.hex_code || "transparent" }}
              />
            ))}
            {swatchPreview.overflow > 0 && (
              <span className="ml-1.5 text-xs font-medium text-muted-foreground">
                +{swatchPreview.overflow}
              </span>
            )}
          </span>
        )}
      </span>
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );

  const body = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-0"
        data-testid="colour-filter-popover"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          <button
            type="button"
            aria-label={t("common.close", "Close") as string}
            onClick={() => setOpen(false)}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto px-2 py-2">
          {colours.map((c) => {
            const idStr = String(c.id);
            const facet = facetByValue[idStr];
            const isRefined = refinedColourIds.includes(idStr);
            const count = facet?.count ?? 0;
            // Disable a row only when Algolia *has* facet data for this
            // colour and the count is zero under the current filter
            // combo. While the index is still catching up (no facet
            // entry for this id at all) we leave the row enabled so
            // the user can still pick it instead of seeing a greyed-out
            // list of every colour.
            const disabled = facet !== undefined && !isRefined && count === 0;
            return (
              <label
                key={c.id}
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm",
                  disabled
                    ? "cursor-not-allowed opacity-40"
                    : "cursor-pointer hover:bg-muted/60",
                )}
              >
                <input
                  type="checkbox"
                  checked={isRefined}
                  disabled={disabled}
                  onChange={() => toggleColour(idStr)}
                  className="rounded border-input accent-primary"
                />
                <span
                  aria-hidden
                  className="inline-block h-4 w-4 shrink-0 rounded-full border border-border"
                  style={{ backgroundColor: c.hex_code || "transparent" }}
                />
                <span className="flex-1 text-foreground">{c.name}</span>
                {facet && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {count}
                  </span>
                )}
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
          <button
            type="button"
            onClick={() => clear()}
            disabled={selectedCount === 0}
            className="text-xs font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            {t("common.clear", "Clear")}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            {t("common.done", "Done")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );

  if (variant === "field") {
    return (
      <div>
        <label className="text-sm font-medium text-foreground">{label}</label>
        <div className="mt-1.5">{body}</div>
      </div>
    );
  }
  return body;
}

export default ColourFilter;
